import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { DollarSign, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';

/**
 * Inline "Set Item Price" dialog used by Restock when a vendor pricing
 * record is missing for the selected item+vendor combination.
 */
export const SetPriceDialog = ({
  open, onOpenChange,
  noPricingItem,
  setPriceData, setSetPriceData,
  savingPrice, onSave,
  showSwitchVendorWarning,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md rounded-xl">
      <DialogHeader>
        <DialogTitle className="font-heading text-xl text-[#1F2937] flex items-center gap-2">
          <DollarSign className="text-[#0776BD]" /> Set Item Price
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <div className="p-3 bg-[#E6F2FA] rounded-lg space-y-2">
          <div>
            <p className="text-xs text-[#6B7280]">Item</p>
            <p className="font-medium text-[#1F2937]">{noPricingItem?.itemName}</p>
          </div>
          <div>
            <p className="text-xs text-[#6B7280]">Vendor</p>
            <p className="font-medium text-[#1F2937]">{noPricingItem?.vendorName}</p>
          </div>
        </div>
        {showSwitchVendorWarning && (
          <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs text-amber-700 flex items-start gap-1.5">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              If pricing is missing, please set it to continue adding this item. It is recommended to complete the current restock before switching vendors.
            </p>
          </div>
        )}
        <div className="space-y-2">
          <Label className="text-sm font-medium text-[#1F2937]">Unit Price *</Label>
          <Input
            type="number" min="0" step="0.01"
            value={setPriceData.unit_price}
            onChange={(e) => setSetPriceData({ ...setPriceData, unit_price: e.target.value })}
            placeholder="0.00"
            className="h-12 rounded-lg border-gray-300"
            autoFocus
            data-testid="set-price-input"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-medium text-[#1F2937]">Vendor SKU</Label>
          <Input
            value={setPriceData.vendor_sku}
            onChange={(e) => setSetPriceData({ ...setPriceData, vendor_sku: e.target.value })}
            placeholder="e.g., HD-12345"
            className="h-12 rounded-lg border-gray-300"
            data-testid="set-price-sku"
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-lg">Cancel</Button>
        <Button onClick={onSave} disabled={savingPrice || !setPriceData.unit_price} className="bg-[#0776BD] hover:bg-[#055A92] rounded-lg gap-2" data-testid="save-price-btn">
          {savingPrice ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
          Save Price
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export default SetPriceDialog;
